import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check } from 'lucide-react';
import AlertDialog from './AlertDialog';
import { useAlertDialog } from '../hooks/useAlertDialog';

interface PurchaseModalProps {
  giftName: string;
  giftPrice: number;
  pixKey: string;
  onClose: () => void;
  onSubmit: (name: string, phone: string, message: string) => Promise<void>;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ 
  giftName, 
  giftPrice, 
  pixKey,
  onClose, 
  onSubmit 
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const { isOpen, title, message: alertMessage, setAlert, clearAlert } = useAlertDialog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !phone) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(name, phone, message);
    } catch (error) {
      console.error('Error submitting purchase:', error);
      setAlert('Erro', 'Erro ao confirmar presente. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyPixKey = async () => {
    try {
      await navigator.clipboard.writeText(pixKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setAlert('Erro', 'Erro ao copiar chave Pix. Por favor, tente novamente.');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div 
          className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-xl font-playfair font-medium text-wedding-blue-800">
              Presentear
            </h3>
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <div className="p-4">
            <div className="mb-6">
              <p className="text-gray-700">Item: <span className="font-medium">{giftName}</span></p>
              <p className="text-gray-700">Valor: <span className="font-medium">R$ {giftPrice.toFixed(2)}</span></p>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">Chave Pix para pagamento:</p>
              <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                <input
                  type="text"
                  value={pixKey}
                  readOnly
                  className="bg-transparent flex-1 text-gray-800 focus:outline-none"
                />
                <button
                  onClick={copyPixKey}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  title="Copiar chave Pix"
                >
                  {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} className="text-gray-600" />}
                </button>
              </div>
              {copied && (
                <p className="text-xs text-green-600 mt-1">Chave Pix copiada!</p>
              )}
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Seu nome
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Seu telefone
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem aos noivos (opcional)
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !name || !phone}
                className="w-full py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Confirmar Presente'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>

      <AlertDialog
        isOpen={isOpen}
        title={title}
        message={alertMessage}
        onClose={clearAlert}
      />
    </>
  );
};

export default PurchaseModal;